import React, { useEffect, useRef, useState } from "react";
import { Input as AntInput } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useDebouncedValue } from "@/frontend/hooks";
import { Spin } from "./Spin";
import { Message } from "./Message";
import type {
  ApiResponse,
  FlatPaginationRequestDTO,
  PaginationResponseDTO,
} from "@/shared/dto";

type FetchFn<T> = (
  params?: FlatPaginationRequestDTO,
) => Promise<ApiResponse<PaginationResponseDTO<T>>>;

export type SearchPaginatedSelectProps<T> = {
  fetchFn: FetchFn<T>;
  filterField?: string;
  placeholder?: string;
  minLength?: number;
  debounceMs?: number;
  pageSize?: number;
  className?: string;
  onSelect: (item: T) => void;
  renderItem?: (item: T) => React.ReactNode;
  getItemKey?: (item: T) => string;
  errorMessage?: string;
  showSearchButton?: boolean;
  allowButtonEmptyQuery?: boolean;
  initialQuery?: string | Record<string, any>;
};

export function SearchPaginatedSelect<T>({
  fetchFn,
  filterField = "name",
  placeholder,
  minLength = 2,
  debounceMs = 300,
  pageSize = 10,
  className = "",
  onSelect,
  renderItem,
  getItemKey,
  errorMessage = "Error al buscar",
  showSearchButton = false,
  allowButtonEmptyQuery = false,
  initialQuery,
}: SearchPaginatedSelectProps<T>) {
  const normalizeInitial = (q?: string | Record<string, any>) => {
    if (q == null) return "";
    if (typeof q === "string") return q;
    if (typeof q === "number" || typeof q === "boolean") return String(q);

    if (typeof q === "object") {
      if (typeof (q as any).name === "string" && (q as any).name.length > 0)
        return (q as any).name;
      if (typeof (q as any).label === "string" && (q as any).label.length > 0)
        return (q as any).label;
      if (typeof (q as any).id === "string" && (q as any).id.length > 0)
        return (q as any).id;
      try {
        const s = (q as any).toString();
        if (s && s !== "[object Object]") return s;
      } catch (e) {}
    }

    return "";
  };

  const [query, setQuery] = useState(normalizeInitial(initialQuery));

  useEffect(() => {
    setQuery(normalizeInitial(initialQuery));
    skipNextSearchRef.current = true;
  }, [initialQuery]);
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const debouncedQuery = useDebouncedValue(query, debounceMs);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const skipNextSearchRef = useRef(false);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setShow(false);
      }
    }

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  useEffect(() => {
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return;
    }

    if (!debouncedQuery || debouncedQuery.length < minLength) {
      setItems([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchFn({ page: 1, pageSize, [filterField]: debouncedQuery })
      .then((resp) => {
        if (resp.success && resp.data) {
          setItems(resp.data.data);
          setTotal(resp.data.pagination.total);
          setPage(resp.data.pagination.page);
          setShow(true);
          setHighlighted(0);
        } else {
          Message.error({ content: resp.error || errorMessage });
          setItems([]);
          setTotal(0);
        }
      })
      .catch((err) => {
        Message.error({
          content: err instanceof Error ? err.message : errorMessage,
        });
        setItems([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [debouncedQuery, fetchFn, filterField, minLength, pageSize, errorMessage]);

  const loadMore = async () => {
    if (loading) return;
    const next = page + 1;
    setLoading(true);
    setShow(true);

    const listEl = listRef.current;
    const prevScroll = listEl?.scrollTop || 0;
    const prevHeight = listEl?.scrollHeight || 0;

    try {
      const resp = await fetchFn({
        page: next,
        pageSize,
        [filterField]: query,
      });
      const payload = resp.data;
      if (resp.success && payload) {
        setItems((s) => [...s, ...payload.data]);
        setPage(payload.pagination.page);
        setShow(true);
      } else {
        Message.error({ content: resp.error || errorMessage });
      }
    } catch (err) {
      Message.error({
        content: err instanceof Error ? err.message : errorMessage,
      });
    } finally {
      setLoading(false);
      if (listEl) {
        requestAnimationFrame(() => {
          const newHeight = listEl.scrollHeight;
          listEl.scrollTop = prevScroll + (newHeight - prevHeight);
        });
      }
    }
  };

  function handleSelect(item: T) {
    const label =
      (item as any)?.name ??
      (item as any)?.label ??
      (getItemKey ? getItemKey(item) : ((item as any)?.id ?? ""));
    skipNextSearchRef.current = true;
    setQuery(String(label));
    setShow(false);
    onSelect(item);
  }

  async function searchFirstPage(q = query) {
    setLoading(true); 
    try {
      const useEmpty = (!q || q.length < minLength) && allowButtonEmptyQuery;
      const resp = await fetchFn({
        page: 1,
        pageSize,
        ...(useEmpty ? {} : { [filterField]: q }),
      });

      if (resp.success && resp.data) {
        setItems(resp.data.data);
        setTotal(resp.data.pagination.total);
        setPage(resp.data.pagination.page);
        setShow(true);
        setHighlighted(0);
      } else {
        if ((!q || q.length < minLength) && !allowButtonEmptyQuery) {
          Message.error({
            content: `Escribe al menos ${minLength} caracteres para buscar`,
          });
        } else {
          Message.error({ content: resp.error || errorMessage });
        }
      }
    } catch (err) {
      Message.error({
        content: err instanceof Error ? err.message : errorMessage,
      });
    } finally {
      setLoading(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!show) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = items[highlighted];
      if (item) handleSelect(item);
    } else if (e.key === "Escape") {
      setShow(false);
    }
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="flex items-center gap-2">
        <AntInput.Search
          className="flex-1"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            if (items.length > 0) setShow(true);
          }}
          onKeyDown={onKeyDown}
          allowClear
          onSearch={async (val: string) => {
            if (show && items.length > 0) {
              const item = items[highlighted];
              if (item) {
                handleSelect(item);
                return;
              }
            }
            await searchFirstPage(val);
          }}
          enterButton={
            showSearchButton ? (
              <SearchOutlined aria-label="Buscar" />
            ) : undefined
          }
        />
      </div>

      {show && (
        <div
          ref={listRef}
          className="absolute left-0 right-0 mt-2 z-50 bg-white border border-gray-200 rounded shadow-lg max-h-72 overflow-auto"
        >
          {loading ? (
            <div className="p-4 flex items-center justify-center">
              <Spin />
            </div>
          ) : items.length === 0 ? (
            <div className="p-3 text-sm text-gray-500">
              No se encontraron resultados
            </div>
          ) : (
            <>
              {items.map((item, idx) => {
                const key = getItemKey
                  ? getItemKey(item)
                  : ((item as any)?.id ?? idx);
                return (
                  <div
                    key={String(key)}
                    role="option"
                    aria-selected={highlighted === idx}
                    onMouseDown={(ev) => {
                      ev.preventDefault();
                      handleSelect(item);
                    }}
                    onMouseEnter={() => setHighlighted(idx)}
                    className={`px-3 py-2 cursor-pointer hover:bg-gray-50 ${highlighted === idx ? "bg-gray-100" : ""}`}
                  >
                    {renderItem
                      ? renderItem(item)
                      : ((item as any)?.name ?? String(key))}
                  </div>
                );
              })}

              {total > items.length && (
                <div className="p-2 border-t border-gray-100 text-center">
                  <button
                    className="text-sm text-blue-600 hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      loadMore();
                    }}
                    disabled={loading}
                  >
                    Cargar más
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
